
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { API_BASE_URL } from '../config';
import { MapPin, Flag, Timer, Zap, ArrowLeft, BarChart2, Activity, Layers, TrendingUp, Gauge, Shield, AlertOctagon, CheckCircle, FlaskConical, Award } from 'lucide-react';

interface ResultExtended {
    user_id: string;
    username: string;
    teamName: string;
    teamColor: string;
    teamLogo?: string;
    position: number;
    status: string;
    points: number;
    sprint_points?: number;
    fastest_lap: boolean;
    clean_race_bonus: boolean;
    is_dotd: boolean; // NEW
    time_diff?: string;
    quali_position?: number;
    sprint_position?: number;
    quali_time?: string;
    sprint_time?: string;
    quali_status?: string;
    sprint_status?: string;
    telemetry_data?: {
        laps?: number[];
        stints?: { tyre: string; laps: number }[];
        penalties?: number; // Penalty Seconds
        consistency?: number;
        theoretical_best?: number;
        race_pace?: number;
        best_sectors?: { s1: number; s2: number; s3: number };
        lap_history?: any[];
        best_lap?: number;
    };
}

const TyreBadge: React.FC<{ type: string, laps?: number, showLapsText?: boolean }> = ({ type, laps, showLapsText = false }) => {
    let color = '#888';
    let letter = 'M';
    const t = String(type).toUpperCase();
    if(t.includes('SOFT') || t === '16' || t === 'S') { color = '#da291c'; letter='S'; }
    else if(t.includes('MEDIUM') || t === '17' || t === 'M') { color = '#ffd700'; letter='M'; }
    else if(t.includes('HARD') || t === '18' || t === 'H') { color = '#f0f0f0'; letter='H'; }
    else if(t.includes('INTER') || t === '7' || t === 'I') { color = '#43b02a'; letter='I'; }
    else if(t.includes('WET') || t === '8' || t === 'W') { color = '#0067a5'; letter='W'; }
    const textColor = (color === '#f0f0f0' || color === '#ffd700') ? 'black' : 'white';
    
    return (
        <div className="flex items-center gap-1 group relative cursor-help justify-center w-full">
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black border border-black/20 shadow-sm z-10 shrink-0" style={{backgroundColor: color, color: textColor}}>{letter}</div>
            {showLapsText && laps && laps > 0 && (
                <span className="text-[10px] font-bold text-white drop-shadow-md hidden md:inline">{laps}L</span>
            )}
            {/* Tooltip */}
            {laps !== undefined && (
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20 border border-gray-700">
                    {laps > 0 ? `${laps} Runden` : 'Genutzt'}
                </div>
            )}
        </div>
    );
};

export const EventDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [results, setResults] = useState<ResultExtended[]>([]);
    const [eventInfo, setEventInfo] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<'RACE' | 'QUALI' | 'SPRINT'>('RACE');

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            apiRequest<any>('events.php?eventId=' + id),
            apiRequest<ResultExtended[]>('results.php?eventId=' + id)
        ]).then(([evData, res]) => {
            // events.php returns an array, extract the first element
            const ev = Array.isArray(evData) ? evData[0] : evData;
            // manually ensure boolean
            if(ev) {
                ev.isTestRace = (ev.isTestRace === true || ev.isTestRace === '1');
                ev.isSprint = (ev.isSprint === true || ev.isSprint === '1' || ev.isSprint === 1);
            }
            setEventInfo(ev);
            
            if (Array.isArray(res)) {
                setResults(res.map(r => {
                    // Boolean fix
                    r.clean_race_bonus = !!r.clean_race_bonus;
                    r.fastest_lap = !!r.fastest_lap;
                    r.is_dotd = !!r.is_dotd;
                    
                    if (typeof r.telemetry_data === 'string') {
                        try { r.telemetry_data = JSON.parse(r.telemetry_data); } catch(e) {}
                    }
                    // Sanitizer for Stints: Filter out -1 or 0 laps
                    if (r.telemetry_data?.stints) {
                        r.telemetry_data.stints = r.telemetry_data.stints.filter(s => s.laps > 0);
                    }
                    return r;
                }));
            }
        }).finally(() => setLoading(false));
    }, [id]);

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const formatTime = (seconds: number) => {
        if (!seconds) return '-';
        const m = Math.floor(seconds / 60);
        const s = (seconds % 60).toFixed(3);
        return `${m}:${s.padStart(6, '0')}`;
    };

    if (loading) return <div className="p-20 text-center text-gray-500 animate-pulse">Lade Ergebnisse...</div>;
    if (!eventInfo) return <div className="p-20 text-center text-gray-500">Event nicht gefunden.</div>;

    const displayResults = results.filter(r => {
        if (tab === 'RACE') return true;
        if (tab === 'QUALI') return r.quali_position && r.quali_position > 0;
        if (tab === 'SPRINT') return r.sprint_position && r.sprint_position > 0;
        return false;
    }).sort((a,b) => {
        if (tab === 'RACE') return a.position - b.position;
        if (tab === 'QUALI') return (a.quali_position||99) - (b.quali_position||99);
        return (a.sprint_position||99) - (b.sprint_position||99);
    });

    const cleanDrivers = results.filter(r => r.clean_race_bonus && r.status === 'FINISHED');
    const penalizedDrivers = results.filter(r => (r.telemetry_data?.penalties || 0) > 0);
    
    // Find Highlights
    const fastestLapDriver = results.find(r => r.fastest_lap);
    const dotdDriver = results.find(r => r.is_dotd);

    const isStatsTab = false;

    // Calculate max laps driven by anyone to normalize bars correctly
    const maxTotalLaps = Math.max(...results.map(r => 
        (r.telemetry_data?.laps?.length || 0) > 0 ? r.telemetry_data!.laps!.length : 
        (r.telemetry_data?.stints?.reduce((sum, s) => sum + s.laps, 0) || 0)
    ), 1);

    return (
        <div className="max-w-7xl mx-auto animate-fade-in space-y-6 pb-20">
            <Link to="/calendar" className="inline-flex items-center text-gray-600 hover:text-white mb-2 transition-colors font-bold uppercase text-[10px] tracking-[0.2em]"><ArrowLeft size={14} className="mr-2"/> Kalender</Link>
            
            {/* Hero Header with Track Background */}
            <div className="relative min-h-[260px] overflow-hidden rounded-[2rem] border border-white/[0.09] bg-[#0a0a10] shadow-[0_30px_90px_rgba(0,0,0,0.32)]">
                {/* Track flag background */}
                {eventInfo.trackFlag && <div className="absolute inset-0 bg-cover bg-center opacity-15 z-0" style={{backgroundImage: `url(${getUrl(eventInfo.trackFlag)})`}}></div>}
                {/* Gradient overlays */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a10] via-[#0a0a10]/80 to-transparent z-[1]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a10] via-transparent to-[#0a0a10]/50 z-[1]" />
                {/* Red accent glow */}
                <div className="absolute top-0 left-0 w-64 h-64 bg-f1-red/10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/4 pointer-events-none z-[1]" />
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-f1-red via-f1-red/50 to-transparent z-[2]" />

                <div className="relative z-10 p-8 md:p-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            {eventInfo.isTestRace ? (
                                <Badge className="bg-blue-900/60 text-blue-200 border-blue-500/50 flex items-center gap-1 text-[10px]"><FlaskConical size={10}/> TEST RACE</Badge>
                            ) : (
                                <Badge className="bg-f1-red/20 text-white border-f1-red/40 text-[10px] font-bold">Runde {eventInfo.roundNumber}</Badge>
                            )}
                            <span className="text-[10px] text-gray-500 font-mono">{new Date(eventInfo.date).toLocaleDateString('de-DE', {day:'2-digit', month:'2-digit', year:'numeric'})} • {new Date(eventInfo.date).toLocaleTimeString('de-DE', {hour:'2-digit', minute:'2-digit', hour12: false})} Uhr</span>
                        </div>
                        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.24em] text-f1-red">Offizieller Rennbericht</p>
                        <h1 className="sdc-display mb-3 text-5xl text-white md:text-7xl" style={{textShadow: '0 4px 20px rgba(0,0,0,0.5)'}}>{eventInfo.trackLocation}</h1>
                        <div className="flex items-center gap-2 text-gray-400 font-bold text-sm"><MapPin size={14}/> {eventInfo.trackName}</div>
                    </div>
                    
                    {/* Track layout image if available */}
                    {eventInfo.trackLayout && (
                        <div className="hidden md:block w-44 h-44 opacity-50 drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]">
                            <img src={getUrl(eventInfo.trackLayout)} className="w-full h-full object-contain invert" alt={`Streckenlayout ${eventInfo.trackLocation}`} onError={event => { if (event.currentTarget.parentElement) event.currentTarget.parentElement.style.display = 'none'; }} />
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 overflow-x-auto rounded-[1.35rem] border border-white/[0.075] bg-[#0e1017] p-1.5">
                <button onClick={() => setTab('RACE')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider whitespace-nowrap flex items-center gap-2 transition-all ${tab==='RACE' ? 'bg-f1-red text-white shadow-lg shadow-f1-red/20' : 'text-gray-600 hover:text-gray-400'}`}><Flag size={13}/> Rennen</button>
                <button onClick={() => setTab('QUALI')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider whitespace-nowrap flex items-center gap-2 transition-all ${tab==='QUALI' ? 'bg-f1-red text-white shadow-lg shadow-f1-red/20' : 'text-gray-600 hover:text-gray-400'}`}><Timer size={13}/> Quali</button>
                {eventInfo?.isSprint && (
                    <button onClick={() => setTab('SPRINT')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider whitespace-nowrap flex items-center gap-2 transition-all ${tab==='SPRINT' ? 'bg-f1-red text-white shadow-lg shadow-f1-red/20' : 'text-gray-600 hover:text-gray-400'}`}><Zap size={13}/> Sprint</button>
                )}
                <div className="w-px bg-gray-800 mx-1"></div>
                <a href={`#/race-analytics`} className="px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider whitespace-nowrap flex items-center gap-2 text-green-500 hover:text-green-400 transition-all"><Activity size={13}/> Race Analytics</a>
            </div>

            {/* RACE HIGHLIGHTS HERO */}
            {tab === 'RACE' && (fastestLapDriver || dotdDriver) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                    {fastestLapDriver && (
                         <div className="group relative flex items-center gap-4 overflow-hidden rounded-[1.5rem] border border-purple-500/30 bg-purple-900/20 p-5">
                             <div className="absolute right-0 top-0 opacity-10 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
                                 <Zap size={100} className="text-purple-500"/>
                             </div>
                             <div className="bg-purple-500/20 p-3 rounded-full border border-purple-500/50 text-purple-400 shrink-0 relative z-10">
                                 <Zap size={24} className="fill-current"/>
                             </div>
                             <div className="relative z-10">
                                 <div className="text-[10px] font-bold text-purple-300 uppercase tracking-widest mb-1">Fastest Lap</div>
                                 <div className="text-xl font-black text-white uppercase italic">{fastestLapDriver.username}</div>
                                 <div className="text-xs text-purple-200">{fastestLapDriver.teamName}</div>
                             </div>
                         </div>
                    )}
                    {dotdDriver && (
                        <div className="group relative flex items-center gap-4 overflow-hidden rounded-[1.5rem] border border-yellow-500/30 bg-yellow-900/20 p-5">
                            <div className="absolute right-0 top-0 opacity-10 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
                                 <Award size={100} className="text-yellow-500"/>
                             </div>
                             <div className="bg-yellow-500/20 p-3 rounded-full border border-yellow-500/50 text-yellow-500 shrink-0 relative z-10">
                                 <Award size={24} className="fill-current"/>
                             </div>
                             <div className="relative z-10">
                                 <div className="text-[10px] font-bold text-yellow-300 uppercase tracking-widest mb-1">Driver of the Day</div>
                                 <div className="text-xl font-black text-white uppercase italic">{dotdDriver.username}</div>
                                 <div className="text-xs text-yellow-200">{dotdDriver.teamName}</div>
                             </div>
                        </div>
                    )}
                </div>
            )}

            <Card className="overflow-hidden rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.23)]">
                {!isStatsTab ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#0e0e16] text-gray-600 text-[9px] font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="p-3 text-center w-12">Pos</th>
                                    <th className="p-3">Fahrer</th>
                                    <th className="p-3 hidden md:table-cell">Team</th>
                                    <th className="p-3 text-right">Zeit/Abstand</th>
                                    <th className="p-3 text-right">Points</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {displayResults.map((res, idx) => {
                                    // Determine specific status for the active tab
                                    const activeStatus = tab === 'RACE' ? res.status : (tab === 'QUALI' ? res.quali_status : res.sprint_status);
                                    const activeTime = tab === 'RACE' ? res.time_diff : (tab === 'QUALI' ? res.quali_time : res.sprint_time);
                                    const activePoints = tab === 'SPRINT' ? Number(res.sprint_points || 0) : (tab === 'RACE' ? (Number(res.points || 0) + Number(res.sprint_points || 0)) : Number(res.points || 0));
                                    const isFinished = activeStatus === 'FINISHED';
                                    
                                    // Fallback: If status says DNF/DSQ but we have a valid time string, show time (common in Quali where DNF means 'did not finish session' but set a lap)
                                    const showTime = isFinished || (activeTime && activeTime.length > 2);

                                    return (
                                        <tr key={idx} className={`hover:bg-gray-800/50 transition-colors ${!isFinished ? 'opacity-70 bg-red-900/10' : ''}`}>
                                            <td className="p-3 text-center font-black text-lg text-gray-300">
                                                {isFinished ? idx+1 : <span className="text-red-500 text-xs font-bold">{activeStatus ? activeStatus.substring(0,3) : 'DNF'}</span>}
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-1 h-8 rounded-full" style={{backgroundColor: res.teamColor}}></div>
                                                    <div className="font-bold text-white uppercase flex items-center gap-2 flex-wrap">
                                                        {res.username}
                                                        
                                                        {/* AWARDS ROW */}
                                                        {tab === 'RACE' && (
                                                            <div className="flex items-center gap-1 ml-1">
                                                                {res.fastest_lap && (
                                                                    <div title="Fastest Lap (+1 Pt)" className="bg-purple-900/30 border border-purple-500/50 rounded-full p-0.5 text-purple-400">
                                                                        <Zap size={10} className="fill-current"/>
                                                                    </div>
                                                                )}
                                                                {res.is_dotd && (
                                                                    <div title="Driver of the Day" className="bg-yellow-900/30 border border-yellow-500/50 rounded-full p-0.5 text-yellow-500">
                                                                        <Award size={10} className="fill-current"/>
                                                                    </div>
                                                                )}
                                                                {res.clean_race_bonus && (
                                                                    <div title="Clean Race Bonus (+1 Pt)" className="bg-blue-900/30 border border-blue-500/50 rounded-full p-0.5 text-blue-400">
                                                                        <Shield size={10} className="fill-current"/>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-3 hidden md:table-cell text-gray-400 text-sm">{res.teamName}</td>
                                            <td className="p-3 text-right font-mono text-gray-300 font-bold">
                                                {showTime ? activeTime : '-'}
                                            </td>
                                            <td className="p-3 text-right">
                                                {tab === 'RACE' ? (
                                                    (activePoints ?? 0) !== undefined ? (
                                                        <div className="flex flex-col items-end gap-1">
                                                            <Badge className="bg-gray-800 text-gray-300 font-mono">
                                                                +{Math.round(activePoints)} Pkt {res.sprint_points && Number(res.sprint_points) > 0 ? 'Gesamt' : ''}
                                                            </Badge>
                                                            {res.sprint_points && Number(res.sprint_points) > 0 ? (
                                                                <div className="text-[10px] text-gray-400 bg-gray-900 px-2 py-0.5 rounded border border-gray-700">
                                                                    Rennen: {Math.round(Number(res.points))} | Sprint: {Math.round(Number(res.sprint_points))}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    ) : null
                                                ) : tab === 'SPRINT' ? (
                                                    (activePoints ?? 0) !== undefined ? (
                                                        <Badge className="bg-gray-800 text-gray-300 font-mono border-gray-700/30">
                                                            +{Math.round(activePoints)} Sprint Pkt
                                                        </Badge>
                                                    ) : null
                                                ) : (
                                                    (activePoints ?? 0) !== undefined ? (
                                                        <Badge className="bg-gray-800 text-gray-300 font-mono">
                                                            +{Math.round(activePoints)} Pkt
                                                        </Badge>
                                                    ) : null
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-8 text-center text-gray-600 text-sm">Keine Ergebnisse verfügbar.</div>
                )}
            </Card>
        </div>
    );
};
